import { applySortStrategy, getPossibleOrientations, SORT_STRATEGIES } from './common.js';

/**
 * First Fit Decreasing Height (FFDH) algorithm.
 * Like shelf packing, but checks ALL existing shelves for each piece before creating a new one.
 */
export const ffdhOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  const sorted = sortStrategy ? applySortStrategy(allPieces, sortStrategy) : applySortStrategy(allPieces, SORT_STRATEGIES.DESCSS);
  const unplacedPieces = [];

  // sheets[]: each has { pieces, shelves: [{ y, height, cursorX }] }
  const sheets = [];

  function newSheet() {
    const s = { id: sheets.length + 1, width: sheetWidth, height: sheetHeight, pieces: [], shelves: [] };
    sheets.push(s);
    return s;
  }

  function tryPlaceOnShelf(sheet, shelf, orient) {
    const x = shelf.cursorX > 0 ? shelf.cursorX + cutWidth : 0;
    if (x + orient.width > sheetWidth) return false;
    const shelfH = shelf.height === 0 ? orient.height : shelf.height;
    if (orient.height > shelfH && shelf.height > 0) return false;
    if (shelf.y + Math.max(shelfH, orient.height) > sheetHeight) return false;

    sheet.pieces.push({ ...orient, x, y: shelf.y });
    shelf.cursorX = x + orient.width;
    if (orient.height > shelf.height) shelf.height = orient.height;
    return true;
  }

  function addShelf(sheet) {
    const y = sheet.shelves.length === 0 ? 0 :
      sheet.shelves[sheet.shelves.length - 1].y + sheet.shelves[sheet.shelves.length - 1].height + cutWidth;
    const shelf = { y, height: 0, cursorX: 0 };
    sheet.shelves.push(shelf);
    return shelf;
  }

  for (const piece of sorted) {
    const orientations = getPossibleOrientations(piece);
    let placed = false;

    // Try ALL existing shelves on ALL existing sheets (first fit)
    for (const sheet of sheets) {
      for (const shelf of sheet.shelves) {
        for (const orient of orientations) {
          if (tryPlaceOnShelf(sheet, shelf, orient)) { placed = true; break; }
        }
        if (placed) break;
      }
      if (placed) break;

      // Try adding a new shelf to this sheet
      if (!placed) {
        const lastShelf = sheet.shelves[sheet.shelves.length - 1];
        const nextY = lastShelf ? lastShelf.y + lastShelf.height + cutWidth : 0;
        for (const orient of orientations) {
          if (nextY + orient.height <= sheetHeight && orient.width <= sheetWidth) {
            const shelf = addShelf(sheet);
            if (tryPlaceOnShelf(sheet, shelf, orient)) { placed = true; break; }
          }
        }
        if (placed) break;
      }
    }

    if (!placed) {
      // New sheet
      const sheet = newSheet();
      const shelf = addShelf(sheet);
      for (const orient of orientations) {
        if (tryPlaceOnShelf(sheet, shelf, orient)) { placed = true; break; }
      }
    }

    if (!placed) unplacedPieces.push(piece);
  }

  const layouts = sheets.filter(s => s.pieces.length > 0).map((s, idx) => ({
    id: idx + 1, width: s.width, height: s.height, pieces: s.pieces,
  }));

  return { layouts, unplacedPieces, unplacedCount: unplacedPieces.length };
};
